import React, { createContext, useContext, useState, useCallback } from 'react';
import MaterialIcon from '../components/ui/MaterialIcon';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

const ToastItem = ({ toast, onRemove }) => {
  const icons = {
    success: 'check_circle',
    error: 'error',
    info: 'info'
  };

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-section-bg border-amber-100/60 text-primary-dark'
  };

  const iconStyles = {
    success: 'text-emerald-700',
    error: 'text-red-600',
    info: 'text-secondary'
  };

  const iconName = icons[toast.type] || 'info';

  return (
    <div className={`flex items-start gap-3 p-4 rounded-2xl border ${styles[toast.type]} warm-tactile-shadow animate-slide-in`}>
      <MaterialIcon name={iconName} size={22} className={`flex-shrink-0 mt-0.5 ${iconStyles[toast.type]}`} />
      <div className="flex-1">
        {toast.title && (
          <h4 className="font-bold text-sm">{toast.title}</h4>
        )}
        <p className="text-sm font-medium leading-relaxed">{toast.message}</p>
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="flex-shrink-0 w-9 h-9 -mr-1 -mt-1 hover:bg-black/10 rounded-full transition-colors flex items-center justify-center"
        aria-label="Dismiss notification"
      >
        <MaterialIcon name="close" size={18} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now().toString();
    const newToast = {
      id,
      type: 'info',
      duration: 4000,
      ...toast
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove after duration
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, newToast.duration);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const success = useCallback((message, title = 'Success') => {
    return addToast({ type: 'success', message, title });
  }, [addToast]);

  const error = useCallback((message, title = 'Error') => {
    return addToast({ type: 'error', message, title, duration: 6000 });
  }, [addToast]);

  const info = useCallback((message, title = '') => {
    return addToast({ type: 'info', message, title });
  }, [addToast]);

  const value = {
    success,
    error,
    info,
    addToast,
    removeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      <div className="fixed left-4 right-4 top-[max(1rem,env(safe-area-inset-top))] z-[90] space-y-2 sm:left-auto sm:right-4 sm:max-w-sm pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem
              toast={toast}
              onRemove={removeToast}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastContext;
